import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { getTimezones } from '../api/models'
import '../styles/components/timezone-select.css'

const MAX_VISIBLE = Infinity

/**
 * Combobox для выбора IANA-таймзоны. Данные берёт с бэкенда (/admin/timezones).
 * Выпадающий список рендерится через портал, чтобы не обрезаться модалкой.
 *
 * @param {Object} props
 * @param {string} props.value
 * @param {function(string): void} props.onChange
 * @param {string} [props.placeholder]
 */
export default function TimezoneSelect({ value, onChange, placeholder = 'Выбрать таймзону…' }) {
  const [timezones, setTimezones] = useState([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState(value || '')
  const [filterQuery, setFilterQuery] = useState('') // при открытии — пустой, показываем весь список
  const [open, setOpen] = useState(false)
  const [dropdownRect, setDropdownRect] = useState(null)
  const containerRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => {
    setLoading(true)
    getTimezones()
      .then((data) => setTimezones(Array.isArray(data) ? data : []))
      .catch(() => setTimezones([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  useEffect(() => {
    if (open) setFilterQuery('')
  }, [open])

  const filtered = filterQuery.trim()
    ? timezones.filter((tz) => tz.toLowerCase().includes(filterQuery.toLowerCase()))
    : timezones

  const visible = filtered.slice(0, MAX_VISIBLE)

  function handleSelect(tz) {
    onChange(tz)
    setQuery(tz)
    setOpen(false)
  }

  function handleInputChange(e) {
    const v = e.target.value
    setQuery(v)
    setFilterQuery(v)
    onChange(v)
    setOpen(true)
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setOpen(false)
      setQuery(value || '')
    }
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault()
      listRef.current?.querySelector('[role="option"]')?.focus()
    }
    if ((e.key === 'Enter' || e.key === ' ') && !open) {
      setOpen(true)
    }
  }

  function handleOptionKeyDown(e, tz, idx) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleSelect(tz)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      listRef.current?.querySelectorAll('[role="option"]')[idx + 1]?.focus()
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (idx === 0) {
        containerRef.current?.querySelector('.tz-select-input')?.focus()
      } else {
        listRef.current?.querySelectorAll('[role="option"]')[idx - 1]?.focus()
      }
    }
    if (e.key === 'Escape') {
      setOpen(false)
      setQuery(value || '')
      containerRef.current?.querySelector('.tz-select-input')?.focus()
    }
  }

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownRect({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    } else {
      setDropdownRect(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        const list = listRef.current
        if (list && list.contains(e.target)) return
        setOpen(false)
        setQuery(value || '')
      }
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open, value])

  const dropdownContent = open && dropdownRect && (
    <ul
      id="tz-listbox"
      ref={listRef}
      role="listbox"
      className="tz-select-dropdown tz-select-dropdown--portal"
      style={{
        position: 'fixed',
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: dropdownRect.width,
        minWidth: dropdownRect.width,
      }}
    >
      {visible.length === 0 && (
        <li className="tz-select-empty">Ничего не найдено</li>
      )}
      {visible.map((tz, idx) => (
        <li
          key={tz}
          role="option"
          tabIndex={0}
          aria-selected={tz === value}
          className={`tz-select-option${tz === value ? ' tz-select-option--selected' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault()
            handleSelect(tz)
          }}
          onKeyDown={(e) => handleOptionKeyDown(e, tz, idx)}
        >
          {tz}
        </li>
      ))}
    </ul>
  )

  return (
    <div ref={containerRef} className={`tz-select${open ? ' tz-select--open' : ''}`}>
      <div className="tz-select-wrap">
        <input
          type="text"
          className="tz-select-input"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={loading ? 'Загрузка…' : placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="tz-listbox"
        />
        <button
          type="button"
          className="tz-select-chevron"
          tabIndex={-1}
          onClick={() => setOpen((p) => !p)}
          aria-hidden
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  )
}
